import React, { FC, useCallback, useMemo, useState } from "react";
import { View as RNView, StyleProp, ViewStyle } from "react-native";
import { View, TOUCHABLE_ROLES } from "@lookiero/aurora";
import "@lookiero/sty-psp-ui";
import { AspectRatioView, LazyImage, useMediaImage, Text } from "@lookiero/sty-psp-ui";
import { useRegisterElementForInView } from "../../../../hooks/useInView";
import { Container, UUID } from "../../../../shared/types";
import { TouchableWithoutFeedback } from "../../../touchableWithoutFeedback/TouchableWithoutFeedback";
import { getAspectRatioByContainer, getPaddingStyleByContainer } from "./LookCollage.definition";
import { style as lookCollageStyle } from "./LookCollage.style";
import { AnimatedDot } from "./animatedDot/AnimatedDot";

const IMAGE_WIDTH = 155;

interface LookForCollage {
  readonly layout: string;
  readonly lookId: UUID;
  readonly items: {
    readonly imageUrl: string;
    readonly purchased?: boolean;
    readonly variantId: UUID;
    readonly container: Container;
  }[];
}

interface LookCollageProp {
  readonly badge: string;
  readonly look: LookForCollage;
  readonly onPress: (variantId: string, purchased?: boolean) => void;
}

const LookCollage: FC<LookCollageProp> = ({ badge, look, onPress }) => {
  const cdnImageUrl = useMediaImage();

  const style = useMemo(() => lookCollageStyle(), []);

  /**
   * TODO
   * We are asuming that this component will be always rendered under InViewProvider.
   * We should decouple it to make it independent.
   * https://lookiero.atlassian.net/browse/LK-31578
   */
  const registerElementForInView = useRegisterElementForInView();

  const handleOnPress = useCallback(
    (purchased: boolean | undefined, variantId: string) => () => {
      onPress(variantId, purchased);
    },
    [onPress],
  );

  const [lookLoaded, setLookLoaded] = useState(false);
  const onEnterViewport = useCallback(
    (inViewPort: boolean) => {
      if (inViewPort === false || lookLoaded === true) {
        return;
      }
      setLookLoaded(true);
    },
    [lookLoaded],
  );

  return (
    <RNView
      ref={(itemRef) => {
        registerElementForInView(look.lookId, itemRef, onEnterViewport);
      }}
      style={style.wrapper}
      onLayout={() => undefined}
    >
      {lookLoaded &&
        look.items.map(({ imageUrl, variantId, purchased, container }) => {
          return (
            <View key={`${look.lookId}-${variantId}`} style={style.itemWrapper}>
              {purchased ? (
                <View>
                  <AspectRatioView
                    aspectRatio={getAspectRatioByContainer(container)}
                    style={[{ width: IMAGE_WIDTH }, getPaddingStyleByContainer(container) as StyleProp<ViewStyle>]}
                  >
                    <LazyImage
                      hiResSrc={cdnImageUrl({ url: imageUrl, width: 600, crop: true })}
                      resizeMode="contain"
                      src={cdnImageUrl({ url: imageUrl, width: IMAGE_WIDTH, dpi: 1, crop: true })}
                      style={{ view: { flex: 1 } }}
                    />
                  </AspectRatioView>
                </View>
              ) : (
                <TouchableWithoutFeedback
                  role={TOUCHABLE_ROLES.button}
                  testID={`look-item-${variantId}`}
                  onPress={handleOnPress(purchased, variantId)}
                >
                  <AnimatedDot style={style.animatedDot} />
                  <AspectRatioView
                    aspectRatio={getAspectRatioByContainer(container)}
                    style={[{ width: IMAGE_WIDTH }, getPaddingStyleByContainer(container) as StyleProp<ViewStyle>]}
                  >
                    <LazyImage
                      hiResSrc={cdnImageUrl({ url: imageUrl, width: 600, crop: true })}
                      resizeMode="contain"
                      src={cdnImageUrl({ url: imageUrl, width: IMAGE_WIDTH, dpi: 1, crop: true })}
                      style={{ view: { flex: 1 } }}
                    />
                  </AspectRatioView>
                </TouchableWithoutFeedback>
              )}
              {badge && purchased && (
                <View style={style.tooltipWrapper}>
                  <Text level={2} style={style.tooltipText} variant="detail">
                    {badge}
                  </Text>
                </View>
              )}
            </View>
          );
        })}
    </RNView>
  );
};

export type { LookForCollage };
export { LookCollage };
